import React, { useState, useEffect, useRef, useCallback } from 'react';

// Searchable manufacturer dropdown component with apply/clear button
const ManufacturerSearchSelect = ({ manufacturers, value, pendingValue, onPendingChange, onApply, onClear, style = {} }) => {
  const [searchText, setSearchText] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  // Get display name for pending value
  const pendingName = pendingValue ? manufacturers.find(m => String(m.manufacturer_id) === String(pendingValue))?.name || '' : '';
  const isActive = value && String(value).trim() !== '';
  const hasUnapplied = String(pendingValue || '') !== String(value || '');

  // Filter manufacturers by search text
  const filtered = searchText.trim()
    ? manufacturers.filter(m => m.name.toLowerCase().includes(searchText.toLowerCase()))
    : manufacturers;

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        if (!pendingValue) setSearchText('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [pendingValue]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && listRef.current && listRef.current.children[highlightIndex]) {
      listRef.current.children[highlightIndex].scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIndex, isOpen]);

  const handleSelect = (mfr) => {
    const newVal = mfr ? String(mfr.manufacturer_id) : '';
    onPendingChange(newVal);
    setSearchText('');
    setIsOpen(false);
  };

  const handleApplyOrClear = () => {
    if (isActive && !hasUnapplied) {
      // Clear
      onPendingChange('');
      onClear();
      setSearchText('');
    } else {
      // Apply — if user typed text but didn't pick from dropdown, auto-select first match
      if (searchText.trim() && !pendingValue) {
        const match = manufacturers.find(m => m.name.toLowerCase().includes(searchText.toLowerCase()));
        if (match) {
          onPendingChange(String(match.manufacturer_id));
          onApply(String(match.manufacturer_id));
          setSearchText('');
          setIsOpen(false);
          return;
        }
      }
      onApply(pendingValue);
      setSearchText('');
      setIsOpen(false);
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center', gap: '0px', ...style }}>
      <input
        type="text"
        value={isOpen ? searchText : pendingName}
        placeholder="All"
        onChange={e => {
          setSearchText(e.target.value);
          setHighlightIndex(0);
          if (!isOpen) setIsOpen(true);
          // If user clears text, clear pending selection too
          if (!e.target.value.trim()) {
            onPendingChange('');
          }
        }}
        onFocus={() => {
          setIsOpen(true);
          setSearchText('');
        }}
        onKeyDown={e => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightIndex(i => Math.min(i + 1, filtered.length));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightIndex(i => Math.max(i - 1, 0));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            if (isOpen && (searchText || !pendingValue)) {
              // Pick from dropdown first
              if (highlightIndex === 0 && !searchText.trim()) {
                handleSelect(null);
              } else if (filtered.length > 0) {
                const idx = highlightIndex > 0 ? highlightIndex - 1 : 0;
                handleSelect(filtered[idx]);
              }
            } else {
              // Apply the pending selection
              handleApplyOrClear();
            }
          } else if (e.key === 'Escape') {
            setIsOpen(false);
            setSearchText('');
          }
        }}
        style={{
          flex: 1,
          minWidth: 0,
          boxSizing: 'border-box',
          padding: '6px',
          border: '1px solid #ddd',
          borderRadius: '4px 0 0 4px',
          fontSize: '13px'
        }}
      />
      <button
        onClick={handleApplyOrClear}
        title={isActive && !hasUnapplied ? 'Clear manufacturer filter' : 'Apply manufacturer filter'}
        style={{
          background: isActive && !hasUnapplied ? '#dc3545' : '#2563eb',
          color: 'white',
          border: 'none',
          borderRadius: '0 2px 2px 0',
          padding: '2px 5px',
          cursor: 'pointer',
          fontSize: '10px',
          fontWeight: '600',
          height: '30px',
          minWidth: '18px',
          transition: 'all 0.2s ease',
          boxShadow: isActive && !hasUnapplied
            ? '0 1px 3px rgba(231, 76, 60, 0.3)'
            : '0 1px 3px rgba(37, 99, 235, 0.3)'
        }}
      >{isActive && !hasUnapplied ? '✕' : '✓'}</button>
      {isOpen && (
        <div
          ref={listRef}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            maxHeight: '200px',
            overflowY: 'auto',
            background: 'white',
            border: '1px solid #ddd',
            borderRadius: '0 0 4px 4px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000
          }}
        >
          <div
            onClick={() => handleSelect(null)}
            style={{
              padding: '6px 8px',
              cursor: 'pointer',
              fontSize: '13px',
              fontStyle: 'italic',
              color: '#666',
              background: highlightIndex === 0 ? '#e8f0fe' : 'transparent'
            }}
            onMouseEnter={() => setHighlightIndex(0)}
          >All Manufacturers</div>
          {filtered.map((m, i) => (
            <div
              key={m.manufacturer_id}
              onClick={() => handleSelect(m)}
              style={{
                padding: '6px 8px',
                cursor: 'pointer',
                fontSize: '13px',
                background: highlightIndex === i + 1 ? '#e8f0fe' : 'transparent',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
              onMouseEnter={() => setHighlightIndex(i + 1)}
            >{m.name}</div>
          ))}
          {filtered.length === 0 && (
            <div style={{ padding: '6px 8px', fontSize: '13px', color: '#999', fontStyle: 'italic' }}>
              No matches
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AmplifierList = ({
  amplifiers,
  filters,
  onFilterChange,
  manufacturers,
  onEdit,
  onDelete,
  renderExtraActions,
  hideDescription,
  hideFilters = false,
  readOnly = false
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Local state for pending filter values (not yet applied)
  const [pendingFilters, setPendingFilters] = useState({
    manufacturer_id: '',
    year_from: '',
    year_to: '',
    model: '',
    serial_number: '',
    description: ''
  });
  
  // Update pending filters when actual filters change (e.g., from clear button)
  useEffect(() => {
    setPendingFilters({
      manufacturer_id: filters.manufacturer_id || '',
      year_from: filters.year_from || '',
      year_to: filters.year_to || '',
      model: filters.model || '',
      serial_number: filters.serial_number || '',
      description: filters.description || ''
    });
  }, [filters]);
  
  // Column resize state - store widths in pixels
  const [columnWidths, setColumnWidths] = useState({
    manufacturer: 140,
    yearRange: 100,
    model: 180,
    serialNumber: 120,
    childUnits: 80,
    description: 200,
    actions: 120
  });
  
  // Resize handling refs
  const resizingColumn = useRef(null);
  const startX = useRef(0);
  const startWidth = useRef(0);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Mouse move handler for column resizing
  const handleMouseMove = useCallback((e) => {
    if (resizingColumn.current) {
      const diff = e.clientX - startX.current;
      const newWidth = Math.max(60, startWidth.current + diff); // Minimum 60px
      setColumnWidths(prev => ({
        ...prev,
        [resizingColumn.current]: newWidth
      }));
    }
  }, []);

  // Mouse up handler to stop resizing
  const handleMouseUp = useCallback(() => {
    resizingColumn.current = null;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'default';
    document.body.style.userSelect = 'auto';
  }, [handleMouseMove]);

  // Apply a specific filter
  const applyFilter = useCallback((filterKey) => {
    onFilterChange(filterKey, pendingFilters[filterKey]);
  }, [pendingFilters, onFilterChange]);

  // Handle Enter key to apply filter
  const handleFilterKeyPress = useCallback((e, filterKey) => {
    if (e.key === 'Enter') {
      applyFilter(filterKey);
    }
  }, [applyFilter]);

  // Start resizing a column
  const startResize = useCallback((columnKey, e) => {
    e.preventDefault();
    resizingColumn.current = columnKey;
    startX.current = e.clientX;
    startWidth.current = columnWidths[columnKey];
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [columnWidths, handleMouseMove, handleMouseUp]);

  // Resize handle component
  const ResizeHandle = ({ columnKey }) => (
    <div
      onMouseDown={(e) => startResize(columnKey, e)}
      style={{
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        width: '6px',
        cursor: 'col-resize',
        background: 'transparent',
        zIndex: 1
      }}
      onMouseEnter={(e) => e.target.style.background = 'rgba(37, 99, 235, 0.3)'}
      onMouseLeave={(e) => e.target.style.background = 'transparent'}
    />
  );

  // Render a filter input with apply button
  const renderFilterInput = (filterKey, placeholder, type = 'text', customPadding = '6px 22px 6px 6px') => {
    const isFilterActive = filters[filterKey] && filters[filterKey].toString().trim() !== '';
    const hasUnappliedChanges = pendingFilters[filterKey] !== filters[filterKey];
    
    return (
      <div style={{ width: '100%', position: 'relative' }}>
        <input
          type={type}
          placeholder={placeholder}
          value={pendingFilters[filterKey] || ''}
          onChange={e => setPendingFilters(prev => ({ ...prev, [filterKey]: e.target.value }))}
          onKeyPress={e => handleFilterKeyPress(e, filterKey)}
          style={{ 
            width: '100%',
            boxSizing: 'border-box', 
            padding: customPadding,
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '13px'
          }}
        />
        <button
          onClick={() => {
            if (isFilterActive && !hasUnappliedChanges) {
              // Clear the filter
              setPendingFilters(prev => ({ ...prev, [filterKey]: '' }));
              onFilterChange(filterKey, '');
            } else {
              // Apply the filter
              applyFilter(filterKey);
            }
          }}
          title={isFilterActive && !hasUnappliedChanges ? 'Clear filter' : 'Apply filter (or press Enter)'}
          style={{
            position: 'absolute',
            right: '2px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: isFilterActive && !hasUnappliedChanges 
              ? '#dc3545' 
              : '#2563eb',
            color: 'white',
            border: 'none',
            borderRadius: '2px',
            padding: '2px 5px',
            cursor: 'pointer',
            fontSize: '10px',
            fontWeight: '600',
            transition: 'all 0.2s ease',
            boxShadow: isFilterActive && !hasUnappliedChanges 
              ? '0 1px 3px rgba(231, 76, 60, 0.3)' 
              : '0 1px 3px rgba(37, 99, 235, 0.3)',
            height: 'calc(100% - 4px)',
            minWidth: '18px'
          }}
          onMouseEnter={(e) => {
            if (isFilterActive && !hasUnappliedChanges) {
              e.target.style.background = '#c0392b';
              e.target.style.boxShadow = '0 2px 4px rgba(231, 76, 60, 0.4)';
            } else {
              e.target.style.background = '#1d4ed8';
              e.target.style.boxShadow = '0 2px 4px rgba(37, 99, 235, 0.4)';
            }
          }}
          onMouseLeave={(e) => {
            if (isFilterActive && !hasUnappliedChanges) {
              e.target.style.background = '#dc3545';
              e.target.style.boxShadow = '0 1px 3px rgba(231, 76, 60, 0.3)';
            } else {
              e.target.style.background = '#2563eb';
              e.target.style.boxShadow = '0 1px 3px rgba(37, 99, 235, 0.3)';
            }
          }}
        >
          {isFilterActive && !hasUnappliedChanges ? '✕' : '✓'}
        </button>
      </div>
    );
  };

  // Word wrap style for cells
  const wrapStyle = {
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    padding: '8px 6px'
  };

  // Header cell style with resize handle
  const headerCellStyle = (width) => ({
    width: `${width}px`,
    minWidth: `${width}px`,
    maxWidth: `${width}px`,
    position: 'relative',
    padding: '10px 6px',
    textAlign: 'left',
    fontWeight: '600',
    borderBottom: '2px solid #e2e8f0',
    background: '#f8fafc'
  });

  // Data cell style with word wrap
  const dataCellStyle = (width) => ({
    width: `${width}px`,
    minWidth: `${width}px`,
    maxWidth: `${width}px`,
    ...wrapStyle
  });

  // Helper to get year display
  const getYearDisplay = (amp) => {
    if (amp.start_year && amp.end_year) {
      return amp.start_year === amp.end_year 
        ? amp.start_year 
        : `${amp.start_year} - ${amp.end_year}`;
    }
    return amp.start_year || amp.end_year || '-';
  };

  // Helper to get manufacturer name
  const getManufacturerName = (amp) => {
    return amp.manufacturer_name || 
      manufacturers.find(m => m.manufacturer_id === amp.manufacturer_id)?.name || 
      '';
  };

  // Mobile card view
  if (isMobile) {
    return (
      <div className="mobile-amp-cards">
        {/* Mobile filter section */}
        {!hideFilters && (
          <div style={{
            background: '#f8f9fa',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem'
          }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr', 
              gap: '0.75rem' 
            }}>
              <ManufacturerSearchSelect
                manufacturers={manufacturers}
                value={filters.manufacturer_id || ''}
                pendingValue={pendingFilters.manufacturer_id || ''}
                onPendingChange={val => setPendingFilters(prev => ({ ...prev, manufacturer_id: val }))}
                onApply={val => {
                  const v = val !== undefined ? val : pendingFilters.manufacturer_id;
                  setPendingFilters(prev => ({ ...prev, manufacturer_id: v }));
                  onFilterChange('manufacturer_id', v);
                }}
                onClear={() => {
                  setPendingFilters(prev => ({ ...prev, manufacturer_id: '' }));
                  onFilterChange('manufacturer_id', '');
                }}
                style={{}}
              />
            </div>
          </div>
        )}
        
        {/* Cards */}
        {amplifiers.map(amp => (
          <div 
            key={amp.amplifier_id} 
            className="amp-card"
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1rem',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              borderLeft: '4px solid #2563eb'
            }}
          >
            <div style={{
              fontSize: '1.1rem',
              fontWeight: '700',
              color: '#333',
              marginBottom: '0.25rem',
              wordBreak: 'break-word'
            }}>
              {amp.model || '-'}
            </div>
            
            <div style={{
              fontSize: '0.9rem',
              color: '#2563eb',
              fontWeight: '600',
              marginBottom: '0.75rem'
            }}>
              {getManufacturerName(amp)}
            </div>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '0.5rem',
              fontSize: '0.85rem',
              color: '#666',
              marginBottom: '0.75rem'
            }}>
              <div>
                <div style={{ color: '#999', fontSize: '0.7rem', textTransform: 'uppercase' }}>Year</div>
                <div>{getYearDisplay(amp)}</div>
              </div>
              <div>
                <div style={{ color: '#999', fontSize: '0.7rem', textTransform: 'uppercase' }}>Model</div>
                <div style={{ wordBreak: 'break-word' }}>{amp.model || '-'}</div>
              </div>
              <div>
                <div style={{ color: '#999', fontSize: '0.7rem', textTransform: 'uppercase' }}>Serial #</div>
                <div>{amp.serial_number || '-'}</div>
              </div>
              <div>
                <div style={{ color: '#999', fontSize: '0.7rem', textTransform: 'uppercase' }}>Variants</div>
                <span style={{ 
                  backgroundColor: amp.child_count > 0 ? '#28a745' : '#6c757d', 
                  color: 'white', 
                  borderRadius: '8px', 
                  padding: '2px 8px',
                  fontWeight: 'bold',
                  fontSize: '0.8em'
                }}>
                  {amp.child_count || 0}
                </span>
              </div>
            </div>
            
            <div style={{
              display: 'flex',
              gap: '0.5rem',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              {renderExtraActions && renderExtraActions(amp)}
              {!readOnly && onEdit && (
                <button 
                  onClick={() => onEdit(amp)}
                  style={{
                    background: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '0.5rem 1rem',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: '600'
                  }}
                >
                  Edit
                </button>
              )}
              {!readOnly && onDelete && (
                <button 
                  onClick={() => onDelete(amp.amplifier_id)}
                  style={{
                    background: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '0.5rem 1rem',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: '600'
                  }}
                  title={`Delete ${amp.name || 'this amplifier'}`}
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        ))}
        
        {amplifiers.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '2rem',
            color: '#666'
          }}>
            No amplifiers found
          </div>
        )}
      </div>
    );
  }

  // Desktop table view
  return (
    <div style={{ overflowX: 'auto' }} className="desktop-amp-table">
      <table style={{ width: 'max-content', minWidth: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={headerCellStyle(columnWidths.manufacturer)}>
              Manufacturer
              <ResizeHandle columnKey="manufacturer" />
            </th>
            <th style={{ ...headerCellStyle(columnWidths.yearRange), textAlign: 'center' }}>
              Year Range
              <ResizeHandle columnKey="yearRange" />
            </th>
            <th style={headerCellStyle(columnWidths.model)}>
              Model
              <ResizeHandle columnKey="model" />
            </th>
            <th style={headerCellStyle(columnWidths.serialNumber)}>
              Serial Number
              <ResizeHandle columnKey="serialNumber" />
            </th>
            <th style={{ ...headerCellStyle(columnWidths.childUnits), textAlign: 'center' }}>
              Variants
              <ResizeHandle columnKey="childUnits" />
            </th>
            {!hideDescription && (
              <th style={headerCellStyle(columnWidths.description)}>
                Description
                <ResizeHandle columnKey="description" />
              </th>
            )}
            <th style={{ ...headerCellStyle(columnWidths.actions), textAlign: 'center' }}>
              {readOnly ? 'View' : 'Actions'}
              <ResizeHandle columnKey="actions" />
            </th>
          </tr>
          {!hideFilters && (
            <tr>
              <th style={{ ...dataCellStyle(columnWidths.manufacturer), background: '#f8fafc', padding: '4px' }}>
                <ManufacturerSearchSelect
                  manufacturers={manufacturers}
                  value={filters.manufacturer_id || ''}
                  pendingValue={pendingFilters.manufacturer_id || ''}
                  onPendingChange={val => setPendingFilters(prev => ({ ...prev, manufacturer_id: val }))}
                  onApply={val => {
                    const v = val !== undefined ? val : pendingFilters.manufacturer_id;
                    setPendingFilters(prev => ({ ...prev, manufacturer_id: v }));
                    onFilterChange('manufacturer_id', v);
                  }}
                  onClear={() => {
                    setPendingFilters(prev => ({ ...prev, manufacturer_id: '' }));
                    onFilterChange('manufacturer_id', '');
                  }}
                />
              </th>
              <th style={{ ...dataCellStyle(columnWidths.yearRange), background: '#f8fafc', textAlign: 'center', padding: '4px' }}>
                {(() => {
                  const isYearActive = (filters.year_from && filters.year_from.toString().trim() !== '') || (filters.year_to && filters.year_to.toString().trim() !== '');
                  const hasUnapplied = pendingFilters.year_from !== (filters.year_from || '') || pendingFilters.year_to !== (filters.year_to || '');
                  return (
                    <div style={{ width: '100%', position: 'relative', display: 'flex', alignItems: 'center', gap: '2px' }}>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="YYYY"
                        maxLength={4}
                        value={pendingFilters.year_from || ''}
                        onChange={e => {
                          const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
                          setPendingFilters(prev => ({ ...prev, year_from: val }));
                        }}
                        onKeyPress={e => {
                          if (!/[0-9]/.test(e.key)) e.preventDefault();
                          if (e.key === 'Enter') {
                            onFilterChange({ year_from: pendingFilters.year_from, year_to: pendingFilters.year_to });
                          }
                        }}
                        style={{
                          width: '50%',
                          minWidth: '38px',
                          boxSizing: 'border-box',
                          padding: '6px 3px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '13px',
                          textAlign: 'center'
                        }}
                      />
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="YYYY"
                        maxLength={4}
                        value={pendingFilters.year_to || ''}
                        onChange={e => {
                          const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
                          setPendingFilters(prev => ({ ...prev, year_to: val }));
                        }}
                        onKeyPress={e => {
                          if (!/[0-9]/.test(e.key)) e.preventDefault();
                          if (e.key === 'Enter') {
                            onFilterChange({ year_from: pendingFilters.year_from, year_to: pendingFilters.year_to });
                          }
                        }}
                        style={{
                          width: '50%',
                          minWidth: '38px',
                          boxSizing: 'border-box',
                          padding: '6px 3px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '13px',
                          textAlign: 'center'
                        }}
                      />
                      <button
                        onClick={() => {
                          if (isYearActive && !hasUnapplied) {
                            setPendingFilters(prev => ({ ...prev, year_from: '', year_to: '' }));
                            onFilterChange({ year_from: '', year_to: '' });
                          } else {
                            onFilterChange({ year_from: pendingFilters.year_from, year_to: pendingFilters.year_to });
                          }
                        }}
                        title={isYearActive && !hasUnapplied ? 'Clear year filter' : 'Apply year filter (or press Enter)'}
                        style={{
                          background: isYearActive && !hasUnapplied ? '#dc3545' : '#2563eb',
                          color: 'white',
                          border: 'none',
                          borderRadius: '2px',
                          padding: '2px 5px',
                          cursor: 'pointer',
                          fontSize: '10px',
                          fontWeight: '600',
                          height: '26px',
                          minWidth: '18px',
                          transition: 'all 0.2s ease',
                          boxShadow: isYearActive && !hasUnapplied
                            ? '0 1px 3px rgba(231, 76, 60, 0.3)'
                            : '0 1px 3px rgba(37, 99, 235, 0.3)'
                        }}
                      >{isYearActive && !hasUnapplied ? '✕' : '✓'}</button>
                    </div>
                  );
                })()}
              </th>
              <th style={{ ...dataCellStyle(columnWidths.model), background: '#f8fafc', padding: '4px' }}>
                {renderFilterInput('model', 'Model')}
              </th>
              <th style={{ ...dataCellStyle(columnWidths.serialNumber), background: '#f8fafc', padding: '4px' }}>
                {renderFilterInput('serial_number', 'Serial #', 'text', '6px 22px 6px 4px')}
              </th>
              <th style={{ ...dataCellStyle(columnWidths.childUnits), background: '#f8fafc', textAlign: 'center', fontSize: '0.85em', color: '#666', padding: '4px' }}>
                {/* No filter for Variants column */}
              </th>
              {!hideDescription && (
                <th style={{ ...dataCellStyle(columnWidths.description), background: '#f8fafc', padding: '4px' }}>
                  {renderFilterInput('description', 'Description')}
                </th>
              )}
              <th style={{ ...dataCellStyle(columnWidths.actions), background: '#f8fafc' }} />
            </tr>
          )}
        </thead>
        <tbody>
          {amplifiers.map(amp => (
            <tr key={amp.amplifier_id} style={{ borderBottom: '1px solid #e2e8f0' }}>
              <td style={dataCellStyle(columnWidths.manufacturer)}>
                {amp.manufacturer_name ||
                  manufacturers.find(m => m.manufacturer_id === amp.manufacturer_id)?.name ||
                  ''}
              </td>
              <td style={{ ...dataCellStyle(columnWidths.yearRange), textAlign: 'center' }}>
                {amp.start_year && amp.end_year ? (
                  amp.start_year === amp.end_year ? 
                    amp.start_year : 
                    `${amp.start_year} - ${amp.end_year}`
                ) : (
                  amp.start_year || amp.end_year || ''
                )}
              </td>
              <td style={dataCellStyle(columnWidths.model)}>{amp.model}</td>
              <td style={dataCellStyle(columnWidths.serialNumber)}>{amp.serial_number}</td>
              <td style={{ ...dataCellStyle(columnWidths.childUnits), textAlign: 'center' }}>
                <span style={{ 
                  backgroundColor: amp.child_count > 0 ? '#28a745' : '#6c757d', 
                  color: 'white', 
                  borderRadius: '8px', 
                  padding: '3px 10px',
                  fontWeight: 'bold',
                  fontSize: '0.85em',
                  display: 'inline-block'
                }}>
                  {amp.child_count || 0}
                </span>
              </td>
              {!hideDescription && <td style={dataCellStyle(columnWidths.description)}>{amp.description}</td>}
              <td style={{ ...dataCellStyle(columnWidths.actions), textAlign: 'center' }}>
                {renderExtraActions && renderExtraActions(amp)}
                {!readOnly && onEdit && (
                  <button onClick={() => onEdit(amp)} style={{ marginLeft: renderExtraActions ? 8 : 0 }}>Edit</button>
                )}
                {!readOnly && onDelete && (
                  <button 
                    onClick={() => onDelete(amp.amplifier_id)} 
                    style={{ 
                      marginLeft: (onEdit || renderExtraActions) ? 8 : 0, 
                      backgroundColor: '#dc3545', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px', 
                      padding: '0.25rem 0.5rem', 
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#c82333'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#dc3545'}
                    title={`Delete ${amp.name || 'this amplifier'}`}
                  >
                    Delete
                  </button>
                )}
                {readOnly && !renderExtraActions && (
                  <span style={{ color: '#666', fontStyle: 'italic' }}>View Only</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export { ManufacturerSearchSelect };
export default AmplifierList;